import { Post, allPosts } from 'contentlayer/generated'
import { useMDXComponent } from 'next-contentlayer/hooks'
import Image from 'next/image'
import { useRouter } from 'next/router'

import useLocalStorage from 'hooks/use-local-storage'
import { useMounted } from 'hooks/use-mounted'
import { formatMdxDate } from 'lib/utils/date'
import { getPlaceholderShimmer } from 'lib/utils/images'

import Box from 'components/atomic/atoms/box'
import Text from 'components/atomic/atoms/text'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import SharedErrorPage from 'components/pages/@shared/error-page'
import sharedComponents from 'components/pages/@shared/mdx'
import ShareButtons from 'components/pages/@shared/share-buttons'
import SharedSubscribeForm, {
  getSubscribeFormSegment,
} from 'components/pages/@shared/subscribe-form'

const Content = ({ changelog }: { changelog: Post }) => {
  const { title, authors, date, imageUrl, description, metaTitle } = changelog
  const mounted = useMounted()
  const [value] = useLocalStorage<boolean>(
    getSubscribeFormSegment('email-list'),
    false,
  )
  const MDXComponent = useMDXComponent(changelog?.body?.code)

  return (
    <LayoutWebsite meta={{ title: metaTitle || title, imageUrl, description }}>
      <Box
        textCenter
        css={{
          py: '$14',
          spaceY: 4,
          bgLinearGradient:
            '0deg, rgba(157, 196, 255, 0.15) 0%, rgba(192, 221, 255, 0) 100%',
          '.dark &': {
            bgLinearGradient:
              '0deg, rgba(21, 68, 138, 0.15) 0%, rgba(5, 64, 133, 0) 100%',
          },
        }}
      >
        <Text
          as="h1"
          color="text1"
          size={{ '@initial': '2xl', '@sm': '4xl' }}
          weight="bold"
          css={{
            container: 'md',
          }}
        >
          {title}
        </Text>
        <Box>
          <Text as="time" color="text2">
            {mounted ? formatMdxDate(date) : '...'}
          </Text>
        </Box>
        <Box flex justify="center" css={{ spaceX: 4 }}>
          {authors?.map(({ name, imageUrl, twitter, socialUrl }, index) => {
            const href = socialUrl?.startsWith('https://')
              ? socialUrl
              : twitter
                ? `https://twitter.com/${twitter}`
                : null

            return (
              <Box
                key={index}
                as={href ? 'a' : 'span'}
                flex="column"
                items="center"
                justify="center"
                css={{
                  spaceY: 1,
                  '&:hover': href ? { color: '$text2' } : {},
                }}
                {...(href ? { href, target: '_blank', rel: 'noopener' } : {})}
              >
                <Box
                  as={Image}
                  src={imageUrl}
                  width={40}
                  height={40}
                  alt={name}
                  css={{ borderRadius: '$round' }}
                />
                <Text size="sm">{name}</Text>
              </Box>
            )
          })}
        </Box>
      </Box>
      <Box css={{ pt: '$12', container: 'md' }}>
        {!!imageUrl && (
          <Box css={{ mb: '$8' }}>
            <Box
              as={Image}
              src={imageUrl}
              width="720"
              height="380"
              placeholder="blur"
              blurDataURL={getPlaceholderShimmer(720, 380)}
              quality="100"
              alt={title}
              css={{ borderRadius: '$lg', objectFit: 'cover' }}
            />
          </Box>
        )}
        <MDXComponent components={sharedComponents} />
        <ShareButtons title={title} />
        {mounted && !value && (
          <Box
            css={{
              borderTop: '1px solid $bg3',
              mt: '$10',
              pt: '$10',
            }}
          >
            <SharedSubscribeForm
              label="Subscribe to changes"
              segment="email-list"
            />
          </Box>
        )}
      </Box>
    </LayoutWebsite>
  )
}

const ChangelogSlugPage = ({ changelog }: { changelog: Post }) => {
  const { isFallback } = useRouter()

  // It means the path does not exist
  if (isFallback || !changelog) {
    return <SharedErrorPage error={404} />
  }

  return <Content changelog={changelog} />
}

export async function getStaticPaths() {
  const paths: string[] = allPosts
    .filter(c => c.category.includes('Changelog'))
    .map(post => post.url)
  return {
    paths,
    fallback: true,
  }
}

export async function getStaticProps({ params }) {
  const changelog = allPosts.find(
    post => post.url === `/changelog/${params.slug}`,
  )

  if (!changelog) {
    return {
      notFound: true,
    }
  }

  const { title, authors, date, imageUrl, description, metaTitle } = changelog

  return {
    props: {
      changelog: {
        title,
        authors,
        date,
        imageUrl,
        description,
        metaTitle: metaTitle ?? null,
        body: { code: changelog?.body?.code },
      },
    },
  }
}

export default ChangelogSlugPage
